/**
 * useProductFilters Hook
 *
 * Manages filtering state and logic for Monitored Products tab.
 * Handles status, product type, and vendor filters.
 * Search query is managed externally (shared across tabs) and passed in.
 */

import { useState, useCallback, useMemo } from 'react';
import type { TrackedProduct } from '../../types/dashboard';

/**
 * Filter state for Monitored Products tab (excluding search which is external)
 */
export interface ProductFilterState {
  statusFilter: string[];
  productTypeFilter: string[];
  productVendorFilter: string[];
}

/**
 * Applied filter for display in Polaris Filters component
 */
export interface AppliedProductFilter {
  key: string;
  label: string;
  onRemove: () => void;
}

/**
 * Filter options extracted from products
 */
export interface ProductFilterOptions {
  statusOptions: string[];
  productTypeOptions: string[];
  productVendorOptions: string[];
}

/**
 * Input parameters for useProductFilters hook
 */
export interface UseProductFiltersInput {
  products: TrackedProduct[];
  debouncedSearchQuery: string;
}

/**
 * Return type for useProductFilters hook
 */
export interface UseProductFiltersReturn {
  // Filter state
  filters: ProductFilterState;

  // Filtered results
  filteredProducts: TrackedProduct[];

  // Applied filters for display
  appliedFilters: AppliedProductFilter[];

  // Filter options
  filterOptions: ProductFilterOptions;

  // Filter setters
  setStatusFilter: (value: string[]) => void;
  setProductTypeFilter: (value: string[]) => void;
  setProductVendorFilter: (value: string[]) => void;

  // Handlers
  handleFiltersClearAll: () => void;
}

/**
 * Hook for managing Monitored Products filtering
 *
 * @param input.products - Tracked products array from loader
 * @param input.debouncedSearchQuery - Debounced search query from parent (shared across tabs)
 * @returns Filter state, handlers, and filtered results
 */
export function useProductFilters({
  products,
  debouncedSearchQuery,
}: UseProductFiltersInput): UseProductFiltersReturn {
  // Filter state (search is external)
  const [statusFilter, setStatusFilter] = useState<string[]>([]);
  const [productTypeFilter, setProductTypeFilter] = useState<string[]>([]);
  const [productVendorFilter, setProductVendorFilter] = useState<string[]>([]);

  // Extract unique values for filter options
  const filterOptions = useMemo((): ProductFilterOptions => {
    const statusOptions = ['Critical', 'Warning', 'Pending data'];
    const productTypeOptions = Array.from(
      new Set(products.map(p => p.product_type).filter(Boolean))
    ) as string[];
    const productVendorOptions = Array.from(
      new Set(products.map(p => p.product_vendor).filter(Boolean))
    ) as string[];

    return {
      statusOptions,
      productTypeOptions,
      productVendorOptions,
    };
  }, [products]);

  // Filter products based on all criteria
  const filteredProducts = useMemo(() => {
    const query = debouncedSearchQuery.toLowerCase();

    return products.filter(product => {
      // Search query filter (using external debounced value)
      const matchesSearch = !query ||
        product.product_title.toLowerCase().includes(query) ||
        (product.product_sku && product.product_sku.toLowerCase().includes(query)) ||
        (product.product_type && product.product_type.toLowerCase().includes(query)) ||
        (product.product_vendor && product.product_vendor.toLowerCase().includes(query));

      // Status filter
      let matchesStatus = statusFilter.length === 0;
      if (statusFilter.length > 0) {
        const alertSeverities = product.alert_severities ? product.alert_severities.split(',') : [];
        const hasCritical = alertSeverities.includes('critical');
        const hasWarning = alertSeverities.includes('warning');
        const hasData = product.variant_count > 0 || product.total_inventory > 0;

        if (statusFilter.includes('critical') && hasCritical) matchesStatus = true;
        if (statusFilter.includes('warning') && hasWarning) matchesStatus = true;
        if (statusFilter.includes('pending data') && !hasData) matchesStatus = true;
      }

      // Product type filter
      const matchesType = productTypeFilter.length === 0 ||
        (product.product_type && productTypeFilter.includes(product.product_type));

      // Product vendor filter
      const matchesVendor = productVendorFilter.length === 0 ||
        (product.product_vendor && productVendorFilter.includes(product.product_vendor));

      return matchesSearch && matchesStatus && matchesType && matchesVendor;
    });
  }, [products, debouncedSearchQuery, statusFilter, productTypeFilter, productVendorFilter]);

  // Build applied filters array for display (excludes search which is shown separately)
  const appliedFilters = useMemo((): AppliedProductFilter[] => {
    const filters: AppliedProductFilter[] = [];

    if (statusFilter.length > 0) {
      filters.push({
        key: 'status',
        label: `Status: ${statusFilter.join(', ')}`,
        onRemove: () => setStatusFilter([]),
      });
    }

    if (productTypeFilter.length > 0) {
      filters.push({
        key: 'productType',
        label: `Type: ${productTypeFilter.join(', ')}`,
        onRemove: () => setProductTypeFilter([]),
      });
    }

    if (productVendorFilter.length > 0) {
      filters.push({
        key: 'productVendor',
        label: `Vendor: ${productVendorFilter.join(', ')}`,
        onRemove: () => setProductVendorFilter([]),
      });
    }

    return filters;
  }, [statusFilter, productTypeFilter, productVendorFilter]);

  // Handler to clear all filters (search is cleared externally)
  const handleFiltersClearAll = useCallback(() => {
    setStatusFilter([]);
    setProductTypeFilter([]);
    setProductVendorFilter([]);
  }, []);

  // Memoize the filters object to prevent unnecessary re-renders
  // This is critical for pagination - without memoization, the useEffect
  // that resets pagination would run on every render
  const filters = useMemo((): ProductFilterState => ({
    statusFilter,
    productTypeFilter,
    productVendorFilter,
  }), [statusFilter, productTypeFilter, productVendorFilter]);

  return {
    // Filter state
    filters,

    // Filtered results
    filteredProducts,

    // Applied filters for display
    appliedFilters,

    // Filter options
    filterOptions,

    // Filter setters
    setStatusFilter,
    setProductTypeFilter,
    setProductVendorFilter,

    // Handlers
    handleFiltersClearAll,
  };
}
